// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../vite-env.d.ts" />
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  inMemoryPersistence,
  getAuth,
  onAuthStateChanged,
  setPersistence,
  type Auth,
  User,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
  memoryLocalCache,
  type Firestore,
  type MemoryLocalCache,
  type PersistentLocalCache,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';

let appInstance: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let dbInstance: Firestore | null = null;
let functionsInstance: Functions | null = null;
let storageInstance: FirebaseStorage | null = null;

/**
 * Returns the appropriate Firestore local cache configuration.
 *
 * Uses persistentSingleTabManager with forceOwnership instead of
 * persistentMultipleTabManager to avoid coordination issues that can
 * cause failures on first page load in private/incognito mode.
 *
 * The forceOwnership option ensures this tab takes control of the
 * IndexedDB cache immediately, which is more reliable for single-page
 * apps where multi-tab coordination isn't critical.
 *
 * If IndexedDB is unavailable (private mode on some browsers), Firestore
 * will automatically fall back to memory-only operation.
 */
function getLocalCacheConfig(): MemoryLocalCache | PersistentLocalCache {
  try {
    return persistentLocalCache({
      tabManager: persistentSingleTabManager({ forceOwnership: true }),
    });
  } catch {
    // Fallback to memory cache if persistent cache setup fails synchronously
    console.info('Persistent cache not available, using memory cache for Firestore');
    return memoryLocalCache();
  }
}

export function initFirebase(): {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
  storage: FirebaseStorage;
  currentUserPromise: Promise<User | null>;
} {
  if (!appInstance) {
    const config = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    };

    // Validate that all required config values are present
    if (!config.projectId) {
      throw new Error(
        'Firebase configuration error: VITE_FIREBASE_PROJECT_ID is missing. ' +
        'Please create a .env file with your Firebase configuration. ' +
        'See .env.example for the required format.'
      );
    }

    if (!config.apiKey || !config.authDomain || !config.appId) {
      throw new Error(
        'Firebase configuration error: Missing required Firebase environment variables. ' +
        'Please check your .env file contains all VITE_FIREBASE_* values.'
      );
    }

    appInstance = getApps().length ? getApps()[0] : initializeApp(config);

    authInstance = getAuth(appInstance);
    functionsInstance = getFunctions(appInstance, 'europe-west1');

    // Try to use local persistence, but fall back to memory in private mode
    setPersistence(authInstance, browserLocalPersistence).catch((error) => {
      console.warn('Local persistence not available (private mode?), using memory:', error);
      // Fall back to in-memory persistence for private/incognito mode
      setPersistence(authInstance!, inMemoryPersistence).catch((memError) => {
        console.error('Memory persistence also failed:', memError);
      });
    });

    // Use appropriate cache based on browsing mode (detected before initialization)
    // This avoids calling initializeFirestore twice which is not supported by Firebase SDK
    dbInstance = initializeFirestore(appInstance, {
      localCache: getLocalCacheConfig(),
    });

    // Initialize Firebase Storage
    storageInstance = getStorage(appInstance);

    // Connect to Firebase emulators when VITE_USE_EMULATORS is set
    if (import.meta.env.VITE_USE_EMULATORS === 'true') {
      const authHost = String(import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST || '127.0.0.1:9099');
      const firestoreHost = String(import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_HOST || '127.0.0.1:8080');
      const functionsHost = String(import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_HOST || '127.0.0.1:5001');

      // Parse host:port for Firestore and Functions
      const [firestoreHostname, firestorePort] = firestoreHost.split(':');
      const [functionsHostname, functionsPort] = functionsHost.split(':');

      const storageHost = String(import.meta.env.VITE_FIREBASE_STORAGE_EMULATOR_HOST || '127.0.0.1:9199');
      const [storageHostname, storagePort] = storageHost.split(':');

      console.info('🔧 Connecting to Firebase Emulators...');
      connectAuthEmulator(authInstance, `http://${authHost}`, { disableWarnings: true });
      connectFirestoreEmulator(dbInstance, firestoreHostname, parseInt(firestorePort, 10));
      connectFunctionsEmulator(functionsInstance, functionsHostname, parseInt(functionsPort, 10));
      connectStorageEmulator(storageInstance, storageHostname, parseInt(storagePort, 10));
      console.info('✅ Connected to Firebase Emulators');
    }
  }

  const app = appInstance!;
  const auth = authInstance!;
  const db = dbInstance!;
  const functions = functionsInstance!;
  const storage = storageInstance!;

  // Don't force anonymous authentication automatically
  // Let components handle authentication as needed
  // For public pages like /order, no auth is needed
  // For protected pages, users will be redirected to /login
  const currentUserPromise = new Promise<User | null>((resolve) => {
    const unsub = onAuthStateChanged(auth, (user) => {
      resolve(user);
      unsub();
    });
  });

  return { app, auth, db, functions, storage, currentUserPromise };
}


